import { Button, Card, Col, enhancedFetch, Form, Icon, Journey, Row, ssrBuilder, SubmitButton } from 'journey-ui'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { Helmet } from 'react-helmet'
import { EnhancedFetchError } from 'journey-ui/dist/lib/EnhancedFetchError'
import { CaretDoubleRight, Plus, PlusCircle, TagChevron, TrashSimple } from 'phosphor-react'
import { AppPage } from '../../components/AppPage'
import { TileCard } from '../../components/Item/TileCard'
import { ItemPageContext, ItemPageContextProps } from '../../contexts/ItemPageContext'
import { awsImageUrl, createCartNotifications, money, sum } from '../../helpers'
import { TileModal } from '../../components/Item/TileModal'
import { AppContext } from '../../contexts/AppContext'
import { AddonCard } from '../../components/Addon/AddonCard'
import { BrandHeader } from '../../components/BrandHeader'
import { ToppingRules } from '../../components/Item/ToppingRules'
import { ItemQuantityInput } from '../../components/Item/ItemQuantityInput'
import { SizeSelection } from '../../components/Item/SizeSelection'
import { WarningCheck } from '../../components/Item/WarningCheck'

export const CardTitle = Journey.div('text-lg my-2 heading-font font-bold text-center')

const OnlyRemoveToppingsLabel = () => (
  <div className="flex flex-wrap justify-center space-x-1">
    You can not add additional toppings, you may only remove existing ones added already.
  </div>
)

const ItemPage: React.FC<ItemPageJson> = (initialItemPageJson) => {
  const router = useRouter()
  const [itemPageJson, setItemPageJson] = useState<ItemPageJson>(initialItemPageJson)
  const { title, description, brand, item, rules, addons, tiles, recipe_hash_id } = itemPageJson

  useEffect(() => {
    setItemPageJson(initialItemPageJson)
  }, [initialItemPageJson])

  const [form, setForm] = useState<ItemForm>({
    size_name: item.size_name,
    price: item.price ?? item.sizes.find((s) => s.name === item.size_name)?.price,
    quantity: item.quantity ?? 1,
    /* Set topping price to 0 if we sent the recipe as the working item. */
    toppings: item.toppings.map((t) => (item.hash_id === recipe_hash_id ? { ...t, price: 0 } : t)),
    /* Only order items will have children, which we map into "addons", basically children = addon */
    children: item.children.map((child) => ({
      price: child.price,
      size_name: child.size_name,
      name: child.name,
      addon_hash_id: child.addon_hash_id,
      hash_id: child.hash_id,
      free_quantity: child.free_quantity,
      quantity: child.quantity,
    })),
  })

  const [tileModal, setTileModal] = useState<Tile>()
  const { refreshCart, order, setCartNotifications, setCartModalOpen } = useContext(AppContext)
  const currentSize = item.sizes.find((s) => s.name === form.size_name)
  /* Is this an orders item? */
  const orderItem = order?.items?.find((i) => i.hash_id === router.query.slug)
  const submitItem = async () => {
    const newOrder = await enhancedFetch<Order>(orderItem ? '/api/order/update-item' : '/api/order/add-item', {
      method: orderItem ? 'PUT' : 'POST',
      body: {
        item_hash_id: orderItem?.hash_id,
        size_hash_id: currentSize.hash_id,
        quantity: form.quantity,
        children: form.children,
        toppings: form.toppings.map((t) => ({
          name: t.name,
          size_name: t.size_name,
          portion_name: t.portion_name,
          tile_hash_id: t.tile_hash_id,
          side: t.side,
          free_quantity: t.free_quantity,
          topping_option_name: t.topping_option_name,
        })),
      },
    })
    refreshCart()

    /* Did we find an item (most likely an addon,attachment) that needs to be addressed? */
    const badItem = newOrder.items.find((i) => i.has_issue && !localStorage.getItem(`item-redirect-${i.hash_id}`))

    setCartNotifications((prevCartNotifications) => createCartNotifications(prevCartNotifications, order, newOrder))

    if (badItem) {
      /* Prevent items from multiple redirects after adding to cart. */
      localStorage.setItem(`item-redirect-${badItem.hash_id}`, '1')

      /* todo: find a better solution than reloading */
      router.push(`/item/${badItem.hash_id}`)
        .then(() => router.reload())
        .catch(console.error)

      return
    }

    router.push('/menu')

    const freeToppingsItem = newOrder.items.find((i) => i.has_free_toppings && !localStorage.getItem(`item-free-topping-popup-${i.hash_id}`))

    if (freeToppingsItem) {
      /* Prevent items with free toppings to popup modal multiple times after adding to cart. */
      localStorage.setItem(`item-free-topping-popup-${freeToppingsItem.hash_id}`, '1')

      /* If we just added an item which has free toppings, show the cart. */
      setCartModalOpen(true)
    }
  }

  const onRemoveItemClick = async () => {
    await enhancedFetch('/api/order/remove-item', {
      body: { item_hash_id: item.hash_id },
      method: 'DELETE',
    })

    refreshCart()

    return router.push('/cart')
  }

  const hasFreeAvailable = useCallback((topping: ItemTopping) => {
    if (topping.portion_name !== 'Regular') {
      return false
    }

    const relevantRules = rules.filter((r) => {
      const ruleContainsToppingTile = r.tile_hash_ids.includes(topping.tile_hash_id)

      if (r.topping_hash_ids?.length > 0) {
        return ruleContainsToppingTile && r.topping_hash_ids.includes(topping.hash_id)
      }

      return ruleContainsToppingTile
    })

    const freeToppingMaxCnt = sum(relevantRules.map((r) => r.free))
    const currentFreeToppingCnt = sum(relevantRules.flatMap((rule) => form.toppings
      .filter((t) => rule.tile_hash_ids.includes(t.tile_hash_id))
      .map((t) => t.free_quantity)))

    return (freeToppingMaxCnt > 0 && freeToppingMaxCnt > currentFreeToppingCnt)
  }, [form.toppings, rules])

  const [busy, setBusy] = useState(false)

  const contextValue: ItemPageContextProps = {
    item, form, setForm, rules, orderItem, addons,
    itemPageJson, setItemPageJson, busy,
    setBusy, tileModal,
    hasFreeAvailable, tiles, setTileModal,
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: item.name,
    image: awsImageUrl(item.picture),
    description,
    mpn: item.hash_id,
    price: money(item.price ?? item.sizes.find((s) => s.name === item.size_name)?.price),
    priceCurrency: 'USD',
    availability: 'InStock',
  }

  let displayPrice = money(form.price + sum(form.children.map((c) => c.price)))

  if (displayPrice !== 'Free') {
    displayPrice = `${displayPrice}`
  }

  return (
    <ItemPageContext.Provider value={contextValue}>
      <AppPage brand={brand} title={title} description={description} backUrl="/menu" className="full-screen">
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        </Helmet>
        <BrandHeader className="h-32">
          <h2 className="text-xl sm:text-3xl z-20 font-bold text-center px-1">
            {item.name}
          </h2>
        </BrandHeader>

        <Form value={form} onChange={setForm} className="space-y-2 p-2 item-edit-form">
          <WarningCheck />
          <SizeSelection />
          {tiles.length > 0 ? (
            <Card>
              <CardTitle>Change Your Options</CardTitle>
              {!item.can_add_toppings ? <OnlyRemoveToppingsLabel /> : null}
              <ToppingRules rules={rules} tiles={tiles} />
              <Row>
                {tiles.sort((a, b) => b.position - a.position)
                  .map((tile) => (
                    <Col key={tile.hash_id} md={6} lg={6} sm={6}>
                      <TileCard tile={tile} />
                    </Col>
                  ))}
              </Row>
            </Card>
          ) : null}
          {addons.length > 0 && (
            <Card>
              <CardTitle>Select Your Addons</CardTitle>
              <Row>
                {addons.map((addon) => (
                  <Col key={addon.hash_id} md={12}>
                    <AddonCard addon={addon} />
                  </Col>
                ))}
              </Row>
            </Card>
          )}
          <Card className="flex flex-col w-full justify-center items-center">
            <CardTitle>Quantity</CardTitle>
            <ItemQuantityInput />
          </Card>

          {orderItem && (
            <Card className="flex justify-between w-full items-center sticky left-0 bottom-12 md:bottom-0">
              <Button className="danger mt-0" onClick={onRemoveItemClick}>
                <Icon src={TrashSimple} />
              </Button>
              <div className="flex flex-col items-center mt-0">
                <SubmitButton disabled={busy} className="flex-grow-0 justify-center w-48 mt-0" onClick={submitItem}>
                  Update (
                  {displayPrice}
                  )
                </SubmitButton>
              </div>
            </Card>
          )}
          {!orderItem && (
            <Card className="items-center sticky left-0 bottom-12 md:bottom-0">
              <SubmitButton disabled={busy} className="mx-auto w-48 mt-0" onClick={submitItem}>
                Add To Cart (
                {displayPrice}
                )
                <Icon src={CaretDoubleRight} className="pb-1 pl-1" />
              </SubmitButton>
            </Card>
          )}
        </Form>
        <TileModal />
      </AppPage>
    </ItemPageContext.Provider>
  )
}

// noinspection JSUnusedGlobalSymbols (PHPStorm doesn't detect NextJS's pages)
export default ItemPage

export async function getServerSideProps(ctx) {
  return ssrBuilder(ctx)
    .setProps((fetcher) => fetcher(`/api/brand/${ctx.req.headers.host}/item-page?slug=${ctx.params.slug}`))
    .onError((error) => {
      if (error instanceof EnhancedFetchError && error.response.status === 404) {
        return {
          notFound: true,
        }
      }

      throw error
    })
    .get()
}
